import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { auth } from "../../services/firebase";
import ProgressButton from "../generic/ProgressButton";
import { useAlert } from "../../hooks/alertHooks";

interface Props {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation("user");
  const { addAlert } = useAlert();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError(t("changePassword.errorTooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("changePassword.errorMismatch"));
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError(t("changePassword.errorNotSignedIn"));
      return;
    }

    setIsSubmitting(true);
    try {
      await user.updatePassword(newPassword);
      addAlert(t("changePassword.successAlert"), "success");
      reset();
      onClose();
    } catch (err: any) {
      const code = err?.code as string | undefined;
      if (code === "auth/weak-password") {
        setError(t("changePassword.errorWeakPassword"));
      } else if (code === "auth/requires-recent-login") {
        setError(t("changePassword.errorRecentLogin"));
      } else {
        setError(err?.message || t("changePassword.errorFallback"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("changePassword.title")}</DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" style={{ marginBottom: 8 }}>
              {error}
            </Typography>
          )}
          <TextField
            label={t("changePassword.newPasswordLabel")}
            type="password"
            fullWidth
            margin="dense"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText={t("changePassword.newPasswordHelper")}
            required
          />
          <TextField
            label={t("changePassword.confirmPasswordLabel")}
            type="password"
            fullWidth
            margin="dense"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t("common:actions.cancel")}
          </Button>
          <ProgressButton
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            inProgress={isSubmitting}
          >
            {t("changePassword.submitButton")}
          </ProgressButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;
